/**
 * TentpoleMomentsSection — business-relevant tentpole moments surfaced INSIDE the Build Wave flow.
 *
 * Personalization:
 *  - Uses the brand's primary_vertical from BrandProfileContext to default the vertical filter.
 *  - Shows a "Top for {brand}" highlighted row of the 2–3 most-relevant upcoming moments.
 *
 * Smart Apply-to-Brief:
 *  - Clicking "Apply to Brief" fetches the full template detail and populates:
 *      campaign name, flight dates (from peak_dates), suggested hashtags, recommended platform(s).
 *
 * Smart Cohort Pre-seed:
 *  - After Activate succeeds, calls onActivated(returnedCreators) so the parent can populate the cohort.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, CalendarDays, Rocket, AlertCircle, Wand2, Star } from 'lucide-react';
import { getTentpoleCalendar, getVerticals, activateTentpoleWave, getTentpoleTemplate } from '../../services/dsp';
import { useBrandProfile } from '../../contexts/BrandProfileContext';
import ActivateWaveModal from './ActivateWaveModal';

const BG = '#0A0A0A';
const CARD = '#121212';
const BORDER = '#1E1E1E';
const T = '#00F5FF';
const P = '#FF6BBD';

// Map peak_dates strings (e.g. "Feb 7-14" or "Jan 15 - Apr 15") → ISO start/end dates using current year.
function parsePeakDates(peakDates) {
  if (!peakDates) return null;
  const year = new Date().getFullYear();
  const months = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  };
  const re = /([A-Z][a-z]{2})\s*(\d{1,2})(?:\s*-\s*(?:([A-Z][a-z]{2})\s*)?(\d{1,2}))?/g;
  const matches = [...peakDates.matchAll(re)];
  if (matches.length === 0) return null;
  const [first, ...rest] = matches;
  const startMonth = months[first[1]];
  const startDay = Number(first[2]);
  if (startMonth === undefined) return null;
  let endMonth = startMonth;
  let endDay = Number(first[4] || first[2]);
  if (rest.length > 0) {
    const last = rest[rest.length - 1];
    endMonth = months[last[1]] ?? startMonth;
    endDay = Number(last[2]);
  } else if (first[3]) {
    endMonth = months[first[3]] ?? startMonth;
  }
  const toIso = (m, d) => {
    const dt = new Date(year, m, d);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  return { start: toIso(startMonth, startDay), end: toIso(endMonth, endDay) };
}

export default function TentpoleMomentsSection({ onApplyMoment = null, onActivated = null }) {
  const { primaryVertical, brandName, secondaryVerticals } = useBrandProfile();
  const [verticals, setVerticals] = useState([]);
  const [activeVertical, setActiveVertical] = useState('all');
  const [calendar, setCalendar] = useState({});
  const [totalMoments, setTotalMoments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [applyingId, setApplyingId] = useState(null);

  // Once primaryVertical loads, default-select it.
  useEffect(() => {
    if (primaryVertical && activeVertical === 'all') setActiveVertical(primaryVertical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryVertical]);

  useEffect(() => {
    let cancelled = false;
    getVerticals()
      .then(res => { if (!cancelled) setVerticals(res.verticals || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    getTentpoleCalendar({ monthsAhead: 6, vertical: activeVertical === 'all' ? null : activeVertical })
      .then(res => {
        if (cancelled) return;
        setCalendar(res.calendar || {});
        setTotalMoments(res.total_moments || 0);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.message || 'Could not load tentpole moments');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeVertical]);

  const flattened = useMemo(() => {
    const list = [];
    Object.entries(calendar).forEach(([month, moments]) => {
      moments.forEach(m => list.push({ ...m, _month: month }));
    });
    return list;
  }, [calendar]);

  // "Top for {brand}" = first 3 moments of the brand's primary vertical
  const topForBrand = useMemo(() => {
    if (!primaryVertical) return [];
    return flattened.filter(m => m.vertical === primaryVertical).slice(0, 3);
  }, [flattened, primaryVertical]);

  const handleApply = async (moment) => {
    if (!onApplyMoment) return;
    setApplyingId(moment.id);
    try {
      // Fetch full template for hashtags + recommended platforms + creator_instructions
      const res = await getTentpoleTemplate(moment.id);
      const template = res.template || {};
      const dates = parsePeakDates(moment.peak_dates || template.peak_dates);
      onApplyMoment({
        id: moment.id,
        momentName: moment.moment,
        vertical: moment.vertical,
        hashtags: template.hashtags || [],
        bestFormats: template.best_formats || [],
        idealNiches: template.ideal_creator_niches || moment.ideal_niches || [],
        idealGenerations: template.ideal_generation || moment.ideal_generations || [],
        shopMyHook: template.shopmy_hook || moment.shopmy_hook || '',
        instructions: template.creator_instructions || '',
        startDate: dates?.start,
        endDate: dates?.end,
      });
    } catch (err) {
      // still apply minimal data so brand gets name + dates
      const dates = parsePeakDates(moment.peak_dates);
      onApplyMoment({ id: moment.id, momentName: moment.moment, vertical: moment.vertical, startDate: dates?.start, endDate: dates?.end });
    } finally {
      setApplyingId(null);
    }
  };

  const renderMomentCard = (m, highlighted = false) => (
    <div key={`${highlighted ? 'top-' : ''}${m.id}`} className="p-3 rounded-lg space-y-1.5 transition-all hover:border-[#00F5FF]/40"
      style={{ background: BG, border: `1px solid ${highlighted ? `${T}40` : BORDER}` }}
      data-testid={`tentpole-moment-${m.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase"
            style={{ background: `${P}15`, color: P, border: `1px solid ${P}30` }}>{m.vertical}</span>
          <h4 className="text-[11px] font-black text-white mt-1 truncate">{m.moment}</h4>
          <p className="text-[9px] text-gray-500">{m._month} • {m.peak_dates}</p>
        </div>
      </div>
      <div className="flex gap-1.5 pt-1">
        {onApplyMoment && (
          <button onClick={() => handleApply(m)} disabled={applyingId === m.id}
            data-testid={`apply-moment-${m.id}`}
            className="flex-1 px-2 py-1.5 rounded text-[9px] font-bold flex items-center justify-center gap-1 transition-all disabled:opacity-50"
            style={{ background: `${T}10`, color: T, border: `1px solid ${T}30` }}>
            {applyingId === m.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Wand2 className="h-2.5 w-2.5" />}
            Apply to Brief
          </button>
        )}
        <button onClick={() => setSelected(m)} data-testid={`activate-moment-${m.id}`}
          className="flex-1 px-2 py-1.5 rounded text-[9px] font-bold text-black flex items-center justify-center gap-1"
          style={{ background: T, boxShadow: `0 0 8px ${T}30` }}>
          <Rocket className="h-2.5 w-2.5" />Activate
        </button>
      </div>
    </div>
  );

  return (
    <div className="rounded-xl p-5 space-y-3" style={{ background: CARD, border: `1px solid ${T}25` }} data-testid="tentpole-moments-section">
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarDays className="h-4 w-4" style={{ color: T }} />
        <h3 className="text-xs font-bold text-white">Relevant Tentpole Moments</h3>
        {primaryVertical && (
          <span className="text-[9px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${T}18`, color: T, border: `1px solid ${T}35` }}>
            Personalized for {brandName || primaryVertical}
          </span>
        )}
        <span className="text-[8px] text-gray-500 ml-auto">Upcoming cultural moments worth activating around</span>
      </div>

      {/* Top-for-brand highlighted strip */}
      {topForBrand.length > 0 && (
        <div className="rounded-lg p-3" style={{ background: `${T}08`, border: `1px solid ${T}30` }} data-testid="top-for-brand">
          <div className="flex items-center gap-1.5 mb-2">
            <Star className="h-3 w-3 fill-current" style={{ color: T }} />
            <p className="text-[10px] uppercase font-black tracking-wider" style={{ color: T }}>Top Moments for {brandName || 'You'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {topForBrand.map(m => renderMomentCard(m, true))}
          </div>
        </div>
      )}

      {/* Vertical filter */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="tentpole-vertical-filter">
        <button onClick={() => setActiveVertical('all')}
          className="px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all"
          style={{ background: activeVertical === 'all' ? `${T}18` : BG, color: activeVertical === 'all' ? T : '#888', border: `1px solid ${activeVertical === 'all' ? `${T}35` : BORDER}` }}
          data-testid="tentpole-vertical-all">
          All ({totalMoments})
        </button>
        {verticals.map(v => {
          const isPrimary = v.vertical === primaryVertical;
          const isSecondary = secondaryVerticals.includes(v.vertical);
          return (
            <button key={v.vertical} onClick={() => setActiveVertical(v.vertical)}
              className="px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all flex items-center gap-1"
              style={{ background: activeVertical === v.vertical ? `${T}18` : BG, color: activeVertical === v.vertical ? T : isPrimary ? T : isSecondary ? P : '#888', border: `1px solid ${activeVertical === v.vertical ? `${T}35` : BORDER}` }}
              data-testid={`tentpole-vertical-${v.vertical.toLowerCase()}`}>
              {isPrimary && <Star className="h-2 w-2 fill-current" />}{v.vertical} ({v.template_count})
            </button>
          );
        })}
      </div>

      {loading && <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" style={{ color: T }} /></div>}

      {error && !loading && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-400" /><p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {!loading && !error && flattened.length === 0 && (
        <p className="text-[11px] text-gray-500 py-3 text-center">No upcoming moments for this vertical.</p>
      )}

      {!loading && flattened.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="tentpole-moment-grid">
          {flattened.slice(0, 9).map(m => renderMomentCard(m))}
        </div>
      )}

      {selected && (
        <ActivateWaveModal
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
          title={`Activate: ${selected.moment}`}
          subtitle={`${selected.vertical} • Peak: ${selected.peak_dates}`}
          templateId={selected.id}
          templateKind="tentpole"
          activateFn={async (payload) => {
            const res = await activateTentpoleWave(payload);
            if (onActivated && res?.creators) onActivated(res.creators);
            return res;
          }}
        />
      )}
    </div>
  );
}
